<script setup>
import {Label} from "./";
import {nextTick, onMounted, ref, computed, reactive} from 'vue';
import {EyeSlashIcon, EyeIcon, XMarkIcon, XCircleIcon, CheckCircleIcon} from "@heroicons/vue/24/outline";
import {InformationCircleIcon} from "@heroicons/vue/24/outline/index.js";
import {__} from "@/Utils/index.js";

const props = defineProps({
    id: String,
    form: Object,
    label: String,
    type: {
        type: String,
        default: 'text',
    },
    required: {
        type: Boolean,
        default: false,
    },
    clearable: {
        type: Boolean,
        default: true
    },
    readonly: {
        type: Boolean,
        default: false
    },
    innerSuffix: String,
    help: String
});

defineOptions({
    inheritAttrs: false
});

const input = ref(null);
const type = ref(props.type);
const rules = reactive([
    { message: __('At least 8 characters.'), validation: false },
    { message: __('At least one lowercase letter.'), validation: false },
    { message: __('At least one uppercase letter.'), validation: false },
    { message: __('At least one number.'), validation: false },
    { message: __('At least one special character.'), validation: false },
])

const allValidationTrue = computed(() => rules.every((rule) => rule.validation));

const hasText = computed(() => {
  const text = props.form?.[props.id];
  return text?.length > 0;
});

onMounted(() => {
    if (input.value.hasAttribute('autofocus')) {
        input.value.focus();
    }
});

function toggleType() {
    type.value = type.value === 'password' ? 'text' : 'password';
}

defineExpose({
    focus: async () => {
        await nextTick();
        input.value.focus()
    }
});

function clearField() {
    props.form[props.id] = '';
}

function validatePassword() {
    const lowercaseRegex = /^(?=.*?[a-z])/;
    const uppercaseRegex = /^(?=.*?[A-Z])/;
    const numberRegex = /^(?=.*?[0-9])/;
    const specialCharactherRegex = /^(?=.*\W)/;

    rules[0].validation = props.form.password.length >= 8;
    rules[1].validation = lowercaseRegex.test(props.form.password);
    rules[2].validation = uppercaseRegex.test(props.form.password);
    rules[3].validation = numberRegex.test(props.form.password);
    rules[4].validation = specialCharactherRegex.test(props.form.password);
}

</script>

<template>
    <div :dusk="$attrs.duskComponentId">
        <div class="flex items-start space-x-2 justify-between">
            <Label
                :dusk="`${$attrs.duskComponentId}-label`"
                v-if="label" :for="id" :value="label" :required :readonly>
            </Label>
            <div v-if="help" class="text-sm">
                <VDropdown
                    :placement="'auto'"
                    :trigger="['hover']"
                >
                    <a><InformationCircleIcon class="size-6" /></a>
                    <template #popper>
                        <div class="max-w-[300px] p-5 bg-slate-900 text-white">
                            {{ help }}
                        </div>
                    </template>
                </VDropdown>
            </div>
        </div>
        <div class="flex items-center">
            <div
                class="flex mx-auto items-center flex-1 focus-within:ring-2 focus-within:ring-purple-600 read-only:focus-within:ring-0 focus-within:rounded-md relative">
                <input
                    v-bind="$attrs"
                    :id="id"
                    :type="type"
                    ref="input"
                    v-model="form[id]"
                    @change="form.clearErrors(id);form.validate(id)"
                    @input = "validatePassword"
                    :readonly="readonly"
                    class="border block w-full border-gray-300 dark:border-slate-600 shadow-sm"
                    :class="[readonly ? 'bg-gray-50 dark:bg-slate-900 opacity-80 hover:cursor-not-allowed focus:ring-0 focus:border-gray-300' : 'dark:bg-slate-700 focus:ring-2 focus:border-purple-600 focus:ring-purple-600', innerSuffix ? 'rounded-l-md' : 'rounded-md']"/>

                <div
                    class="px-2 py-2 absolute top-1/2 -translate-y-1/2  flex items-center right-8">
                    <XMarkIcon v-if="!readonly && clearable && form[id]" class="size-5 text-gray-400 cursor-pointer"
                               @click="clearField"/>
                </div>

                <div
                    v-if="innerSuffix"
                    class="pointer-events-none flex items-center p-2.5 bg-gray-50 border-y border-r border-gray-300 rounded-e-md">
                    <span class="text-gray-500 text-sm">{{ innerSuffix }}</span>
                </div>

                <component :is="type === 'password' ? EyeSlashIcon : EyeIcon"
                           class="size-6 absolute right-4 cursor-pointer text-gray-400"
                           v-if="hasText"
                           @click="toggleType"/>
            </div>
        </div>
        <div v-auto-animate>
            <ul v-if="form.password.length != 0 && !allValidationTrue" class="errors">
                <li v-for="(rule, index) in rules" :key="index">
                    <span :class="{valid: rule.validation}">
                        <XCircleIcon v-if="!rule.validation" class="size-6 text-red-600"/>
                        <CheckCircleIcon v-if="rule.validation" class="size-6 text-green-600"/>
                        {{ rule.message }}
                    </span>
                </li>
            </ul>
            
        </div>
    </div>
</template>

<style>
.errors {
    list-style-type: none;
    margin-top: 12px;
    padding: 0px;
}

.errors li span {
    display: flex;
    align-items: center;
    margin-top: 2px;
    gap: 6px;
}

.errors li span.valid {
    color: rgb(22 163 74 / var(--tw-text-opacity, 1));
}

.errors li span:not(.valid) {
    color: rgb(220 38 38 / var(--tw-text-opacity, 1));
}
</style>